import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AssetService } from '../services/asset/asset.service';
import { MastersService } from '../services/masters/masters.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom, Subscription } from 'rxjs';
import { ListStateService } from '../services/list-state/list-state.service';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { openInNewTab } from '../utils/url.utils';
import { AssetDepreciationModalComponent } from '../asset-depreciation-modal/asset-depreciation-modal';
import { ButtonModule } from 'primeng/button';
import { PageActionService } from '../services/page-action/page-action.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'lib-asset-dashboard', // Keeping selector same to avoid breaking changes
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule, AssetDepreciationModalComponent, ButtonModule],
  providers: [DialogService],
  templateUrl: './asset-dashboard.html',
  styleUrls: ['./asset-dashboard.css'],
})
export class AssetDashboard implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  tableData: any[] = [];
  totalCount = 0;
  isLoading: boolean = false;
  pageIndex: number = 1;
  rowsPerPage: number = 10;
  totalPages: number = 0;
  columnPreferenceId?: string;
  exportFilename = `assets_${new Date().toISOString().slice(0, 10)}`;
  showDepreciationModal = false;

  // Filter values
  status: string = '';
  assetGroupId: string = '';
  subsidiaryId: string = '';
  locationId: string = '';
  searchQuery: string = '';

  // Options
  assetGroupOptions: any[] = [];
  subsidiaryOptions: any[] = [];
  locationOptions: any[] = [];
  private subsidiariesRawData: any[] = [];
  statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Assigned', value: 'assigned' },
    { label: 'Under Maintenance', value: 'under_maintenance' },
    { label: 'Written Off', value: 'disposed' },
    { label: 'Transferred', value: 'transferred' },
    { label: 'Removed', value: 'removed' }
  ];

  filterConfigs: FilterConfig[] = [];

  // Column configurations - initially empty, will be loaded from backend or default
  columnConfigs: ColumnConfig[] = [];

  actionConfigs: ActionConfig[] = [
    { key: 'view', label: 'View', tooltip: 'View', severity: 'secondary', svgIcon: SVG_ICONS.VIEW },
    { key: 'edit', label: 'Edit', tooltip: 'Edit', severity: 'secondary', svgIcon: SVG_ICONS.EDIT },
    { key: 'delete', label: 'Delete', tooltip: 'Delete', severity: 'secondary', svgIcon: SVG_ICONS.DELETE },
  ];

  constructor(
    private assetService: AssetService,
    private mastersService: MastersService,
    private subsidiaryService: SubsidiaryService,
    private router: Router,
    private toasterService: ToasterService,
    private dialogService: DialogService,
    private exportApiService: ExportApiService,
    private pageActionService: PageActionService
  ) {}

  private destroy$ = new Subject<void>();

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
    this.pageActionService.setActions([]);
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnInit() {
    // Handle "Run Depreciation" action from header actionButtons
    this.pageActionService.setActions([]);
    this.pageActionService.handleActionsUntil(this.destroy$, {
      'run-depreciation': () => {
        this.showDepreciationModal = true;
      },
    });

    try {
      await Promise.all([this.loadMasters(), this.loadSubsidiaries()]);
      this.initializeFilterConfigs();
      
      // Subscribe to URL page changes
      this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
        this.loadAssets(page);
      });
    } catch (error) {
      console.error('Error in ngOnInit:', error);
      this.isLoading = false;
    }
  }

  async loadMasters() {
    try {
      const res = await lastValueFrom(this.mastersService.getMasters(['asset-groups']));
      if (res?.data?.['asset-groups']) {
        this.assetGroupOptions = res.data['asset-groups'].map((g: any) => ({
          label: g.name,
          value: g.id
        }));
      }
    } catch (e) {
      console.error('Error loading masters', e);
    }
  }

  async loadSubsidiaries() {
    try {
      const res = await lastValueFrom(this.subsidiaryService.getSubsidiaries());
      const data = res?.data?.subsidiaries || res?.data || [];
      if (Array.isArray(data)) {
        this.subsidiariesRawData = data;
        this.subsidiaryOptions = data.map((s: any) => ({
          label: s.name,
          value: s.id
        }));
      }
    } catch (e) {
      console.error('Error loading subsidiaries', e);
    }
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'searchQuery',
        type: 'text',
        placeholder: 'Search by Code/Name',
        value: this.searchQuery,
        showInAccordion: true,
      },
      {
        key: 'status',
        type: 'select',
        placeholder: 'Status',
        options: this.statusOptions,
        value: this.status,
        showInAccordion: true,
      },
      {
        key: 'assetGroupId',
        type: 'select',
        placeholder: 'Asset Group',
        options: this.assetGroupOptions,
        value: this.assetGroupId,
        showInAccordion: true,
      },
      {
        key: 'subsidiaryId',
        type: 'select',
        placeholder: 'Subsidiary',
        options: this.subsidiaryOptions,
        value: this.subsidiaryId,
        showInAccordion: true,
      },
      {
        key: 'locationId',
        type: 'select',
        placeholder: 'Location',
        options: this.locationOptions,
        value: this.locationId,
        showInAccordion: true,
      }
    ];
  }

  // Update location options based on selected subsidiary
  updateLocationOptions(subsidiaryId: any) {
    if (!subsidiaryId) {
      this.locationOptions = [];
    } else {
      const subsidiary = this.subsidiariesRawData.find((s: any) => String(s.id) === String(subsidiaryId));
      if (subsidiary?.tax_information && Array.isArray(subsidiary.tax_information)) {
        this.locationOptions = subsidiary.tax_information
          .filter((t: any) => !t.is_deleted)
          .map((t: any) => ({
            label: t.address || `Location ${t.id}`,
            value: t.id
          }));
      } else {
        this.locationOptions = [];
      }
    }
    // Update filterConfigs for location
    const locationFilterIndex = this.filterConfigs.findIndex(f => f.key === 'locationId');
    if (locationFilterIndex !== -1) {
      this.filterConfigs[locationFilterIndex].options = this.locationOptions;
      this.filterConfigs = [...this.filterConfigs]; // Trigger change detection
    }
  }

  async loadAssets(page: number = 1, skipLoading: boolean = false) {
    if (!skipLoading) this.isLoading = true;
    this.pageIndex = page;

    try {
      const filters: any = {
        limit: this.rowsPerPage,
        offset: (page - 1) * this.rowsPerPage,
        get_references: 'true',
      };

      if (this.status) filters.status = this.status;
      if (this.assetGroupId) filters.asset_group_id = this.assetGroupId;
      if (this.subsidiaryId) filters.subsidiary_id = this.subsidiaryId;
      if (this.locationId) filters.location_id = this.locationId;
      if (this.searchQuery) filters.search = this.searchQuery;

      const response = await lastValueFrom(this.assetService.getAssets(filters));
      const data = response?.data || response || {};
      const assets = Array.isArray(data) ? data : (data.assets || []);

      // Extract column preference from response
      if (data.column_preference) {
        const columnPreference = data.column_preference;
        this.columnPreferenceId = columnPreference.id ? String(columnPreference.id) : undefined;

        // Update columnConfigs with columns_list from preference
        if (columnPreference.columns_list && Array.isArray(columnPreference.columns_list)) {
          this.columnConfigs = columnPreference.columns_list.map((col: any) => ({
            key: col.key,
            header: col.header || col.label,
            type: col.type,
            ellipsis: col.ellipsis,
            align: col.align,
            is_selected: col.is_selected !== false,
            customField: col.customField || col.is_custom_field || false,
          }));
        }
      }

      this.tableData = assets.map((asset: any) => ({
        ...asset,
        ...(asset.custom_fields || {}),
        status: this.formatStatus(asset.status),
        raw_status: asset.status // Keep raw status for logic if needed
      }));

      this.totalCount = data.total || this.tableData.length;
      this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage) || 1;

    } catch (e) {
      console.error('Failed to load assets', e);
      this.toasterService.error('Failed to load assets');
      this.tableData = [];
    } finally {
      if (!skipLoading) this.isLoading = false;
    }
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id;
    this.columnConfigs = event.columns;
    this.loadAssets(this.pageIndex);
  }

  formatStatus(status: string): string {
    if (!status) return '-';
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  onFilterEvent(event: FilterEvent) {
    this.status = event.data?.status || '';
    this.assetGroupId = event.data?.assetGroupId || '';
    this.searchQuery = event.data?.searchQuery || '';

    // Check if subsidiary changed to update location options
    const newSubsidiaryId = event.data?.subsidiaryId || '';
    if (newSubsidiaryId !== this.subsidiaryId) {
      this.subsidiaryId = newSubsidiaryId;
      this.locationId = ''; // Reset location when subsidiary changes
      this.updateLocationOptions(this.subsidiaryId);
    }

    this.locationId = event.data?.locationId || '';
    
    this.listStateService.clearPage();
    this.loadAssets(1);
  }

  onActionClicked(event: ActionEvent) {
    if (event.action === 'add') {
      this.router.navigate(['/assets/add']);
      return;
    }

    const asset = event.row;
    if (!asset?.id) return;

    switch (event.action) {
      case 'view':
        this.router.navigate(['/assets', asset.id], { queryParams: { mode: 'view' } });
        break;
      case 'edit':
        this.router.navigate(['/assets', asset.id], { queryParams: { mode: 'edit' } });
        break;
      case 'delete':
        this.deleteAsset(asset.id);
        break;
    }
  }

  onPageChanged(page: number) {
    this.listStateService.setPage(page);
  }

  openDepreciationModal() {
    this.showDepreciationModal = true;
  }

  onDepreciationApplied() {
    this.loadAssets(this.pageIndex);
  }

  async deleteAsset(id: string) {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      await lastValueFrom(this.assetService.deleteAsset(id));
      this.toasterService.success('Asset deleted successfully');
      this.loadAssets(this.pageIndex);
    } catch (e) {
      console.error('Delete failed', e);
      this.toasterService.error('Failed to delete asset');
    }
  }

  onColumnsSelected(cols: string[]) {
    // Handle column selection persistence if needed
  }

  exportCsv() {
    this.openExportDialog('csv');
  }

  exportXlsx() {
    this.openExportDialog('excel');
  }

  exportPdf() {
    window.print();
  }

  /**
   * Opens the export dialog with filter and column options.
   * @param format Default export format
   */
  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Assets',
      width: '500px',
      data: {
        module: 'asset',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        defaultFormat: format
      }
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  /**
   * Gets the current filter values for the export.
   */
  private getCurrentFilters(): Record<string, any> {
    return {
      status: this.status || undefined,
      asset_group_id: this.assetGroupId || undefined,
      subsidiary_id: this.subsidiaryId || undefined,
      location_id: this.locationId || undefined,
      search: this.searchQuery || undefined
    };
  }

  /**
   * Starts the export job via the API.
   */
  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started',
          'Your export is processing. Click here to view progress →',
          5000
        );
        // Auto-open exports page in new tab after a short delay
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      }
    });
  }
}
